import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { apiGet } from '@/lib/http/client';

import { proposalQueryKeys } from '../query/keys';
import type { LinkedInDeliveryState } from './deliveryTypes';
import { NON_TERMINAL_STATUSES } from './deliveryTypes';

const POLL_INTERVAL_MS = 15000;

// After a proposal is approved, its outbound row is created asynchronously
// (the send action executes a moment later), so the first fetch(es) can come
// back empty. Keep polling this many times so the newly-queued row is
// discovered without a manual refresh, then give up — a proposal that never
// produces a LinkedIn send would otherwise poll forever. ~2 min at
// POLL_INTERVAL_MS.
const BOOTSTRAP_POLL_ATTEMPTS = 8;

// The endpoint caps each request at 200 ids (later ids are silently dropped),
// so chunk the ids into batches of this size and merge the results. Keeps every
// action's delivery state visible on proposals with more than 200 actions.
const MAX_IDS_PER_REQUEST = 200;

type DeliveryStatesByActionId = Record<number, LinkedInDeliveryState>;

async function fetchDeliveryStatesBatch(
  orgId: number,
  ids: number[]
): Promise<LinkedInDeliveryState[]> {
  const resp = await apiGet<{ states: LinkedInDeliveryState[] }>(
    `/api/orgs/${orgId}/browser-extension/linkedin-outbound/delivery-status/?proposed_action_ids=${ids.join(',')}`,
    undefined,
    10000
  );
  return Array.isArray(resp?.states) ? resp.states : [];
}

async function fetchDeliveryStates(
  orgId: number,
  ids: number[]
): Promise<DeliveryStatesByActionId> {
  const batches: number[][] = [];
  for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) {
    batches.push(ids.slice(i, i + MAX_IDS_PER_REQUEST));
  }
  const results = await Promise.all(
    batches.map(batch => fetchDeliveryStatesBatch(orgId, batch))
  );
  return Object.fromEntries(results.flat().map(s => [s.proposed_action_id, s]));
}

/**
 * Computes the refetch interval for the delivery-status query.
 *
 * Polls while any known state is non-terminal ('queued' or 'claimed') so a late
 * Failed/Expired transition becomes visible.
 *
 * After approval (`expectsDeliveryRows`), an outbound row is created
 * asynchronously — and in a multi-send proposal a later row can appear only
 * after an earlier row has already settled to a terminal state. So while the
 * proposal still expects rows, keep polling through the whole bounded bootstrap
 * window even once every present row is terminal, so a late-created row is
 * discovered without a manual refresh. The frontend cannot know how many send
 * rows to expect — a LinkedIn-channel proposal may contain no send at all, or
 * mix a send with CRM/timeline actions that never produce a row, and precise
 * per-send gating needs the `linkedin_send` action type, which is not yet part
 * of `ProposedActionTypeName`. The window is therefore bounded by
 * BOOTSTRAP_POLL_ATTEMPTS: a proposal that produces no (further) row stops
 * polling after the window instead of continuing forever.
 *
 * Returns `false` (stop polling) once every present row is terminal and the
 * bootstrap window is exhausted (or, when no rows are expected, immediately once
 * every present row is terminal).
 *
 * Exported for unit testing.
 */
export function computeDeliveryPollInterval(
  states: DeliveryStatesByActionId | undefined,
  expectsDeliveryRows: boolean,
  fetchAttempts: number
): number | false {
  const anyNonTerminal =
    states != null &&
    Object.values(states).some(s => NON_TERMINAL_STATUSES.has(s.status));
  if (anyNonTerminal) {
    return POLL_INTERVAL_MS;
  }

  // Bootstrap window: rows are created asynchronously after approval, and a
  // multi-send proposal can create a later row only after an earlier row has
  // already reached a terminal state. Poll through the whole bounded window
  // while rows are still expected — not just while zero rows exist — so that
  // late row is still discovered once earlier rows have settled.
  if (expectsDeliveryRows && fetchAttempts < BOOTSTRAP_POLL_ATTEMPTS) {
    return POLL_INTERVAL_MS;
  }

  return false;
}

/**
 * Fetches LinkedIn outbound delivery states for a set of proposed-action ids
 * and returns them keyed by proposed_action_id.
 *
 * `expectsDeliveryRows` should be true once the proposal is approved/executing
 * (an outbound row is imminent). It is part of the query key so approval starts
 * a fresh bootstrap window, and it drives the bounded bootstrap poll in
 * {@link computeDeliveryPollInterval} so a row created shortly after approval is
 * discovered without waiting for a window focus or page refresh.
 */
export function useLinkedInDeliveryStates(
  orgId: number,
  actionIds: number[],
  expectsDeliveryRows: boolean
): DeliveryStatesByActionId {
  const ids = useMemo(
    () => [...new Set(actionIds)].sort((a, b) => a - b),
    [actionIds]
  );

  const { data } = useQuery({
    queryKey: proposalQueryKeys.linkedinDeliveryStatus(
      orgId,
      ids,
      expectsDeliveryRows
    ),
    queryFn: () => fetchDeliveryStates(orgId, ids),
    enabled: ids.length > 0,
    retry: false,
    refetchOnWindowFocus: true,
    refetchInterval: query =>
      computeDeliveryPollInterval(
        query.state.data,
        expectsDeliveryRows,
        query.state.dataUpdateCount + query.state.errorUpdateCount
      ),
  });

  return data ?? {};
}
